import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useEffect } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { useCancelAppointment } from '@/modules/appointment/hooks/useCancelAppointment'
import { useToast } from '../../hooks/useToast'

import type { InheritableModalProps } from '@/@bothrs-ui/web/components/modal/Modal.props'
import type { Appointment } from '@/modules/appointment/types/appointment.types'

type CancelAppointmentModalProps = InheritableModalProps & {
  appointment?: Appointment
  onAction: () => void
  onCloseAfterCancel?: () => void
  onCancelling: () => void
}

const CancelAppointmentModal = ({ appointment, onAction, ...props }: CancelAppointmentModalProps) => {
  const { execute, isLoading, value, error, status } = useCancelAppointment()
  const { showSuccessToast } = useToast()
  const theme = useTheme()

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleClose() {
    if (!props.disabled && props.onClose) {
      if (!value) {
        props.onClose()
      } else if (props.onCloseAfterCancel) {
        props.onCloseAfterCancel()
      }
    }
  }

  function cancelAppointment() {
    if (appointment) {
      props.onCancelling()
      execute(appointment)
    }
  }

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (status === 'success') {
      showSuccessToast({
        message: t({
          id: 'appointments.toast.cancelled',
          comment: 'Appointment cancelled toast message',
          message: 'Je afspraak is geannuleerd.',
        }),
        duration: 8000,
      })
    }
  }, [showSuccessToast, status])

  // Render Methods
  // ------------------------------------------------------------------------- /
  function renderInitialContent() {
    return (
      <>
        <FontAwesomeIcon icon={['fal', 'question-circle']} fontSize={24} />

        <Margin mt={theme.UI.Spacing.Space5} />

        <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
          {t({
            id: 'appointments.modal.cancel.title',
            comment: 'Modal title',
            message: 'Afspraak annuleren',
          })}
        </H3>

        <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'appointments.modal.cancel.body',
            comment: 'Modal body',
            message: `Ben je zeker dat je je afspraak wil annuleren? Deze actie kan
              niet ongedaan gemaakt worden.`,
          })}
        </P>

        <Button variant='PrimaryWarning' onClick={cancelAppointment} disabled={isLoading}>
          {t({
            id: 'appointments.modal.cancel.primary',
            comment: 'Modal primary button',
            message: 'Afspraak annuleren',
          })}
        </Button>
        <Margin mt={theme.UI.Spacing.Space2} />
        <Button variant='Tertiary' onClick={handleClose} isLoading={isLoading}>
          {i18n._(globalDefinitions.goBack)}
        </Button>
      </>
    )
  }

  function renderCancelledContent() {
    return (
      <>
        <FontAwesomeIcon icon={['fal', 'check-circle']} fontSize={24} />

        <Margin mt={theme.UI.Spacing.Space5} />

        <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
          {t({
            id: 'appointments.modal.cancelled.title',
            comment: 'Modal title',
            message: 'Afspraak geannuleerd',
          })}
        </H3>

        <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'appointments.modal.cancelled.subtitle',
            comment: 'Modal description',
            message: 'Wil je meteen een nieuwe afspraak boeken met een dokter?',
          })}
        </P>

        <Button variant='Primary' onClick={onAction}>
          {t({
            id: 'appointments.modal.cancelled.primary',
            comment: 'Modal primary button',
            message: 'Nieuwe afspraak boeken',
          })}
        </Button>
        <Margin mt={theme.UI.Spacing.Space2} />
        <Button variant='Tertiary' onClick={handleClose}>
          {t({
            id: 'globals.close',
            comment: 'Global for close',
            message: 'Sluiten',
          })}
        </Button>
      </>
    )
  }

  function renderErrorContent() {
    return (
      <>
        <FontAwesomeIcon icon={['fal', 'exclamation-triangle']} fontSize={24} />

        <Margin mt={theme.UI.Spacing.Space5} />

        <H3 textAlign='center' color={theme.Colors.Neutrals.Darkest}>
          {t({
            id: 'appointments.modal.cancel.error.title',
            comment: 'Modal title',
            message: 'Afspraak annuleren',
          })}
        </H3>

        <P type='BodyLargeRegular' textAlign='center' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'appointments.modal.cancel.error.body-error',
            comment: 'Message when the appointment could not be cancelled.',
            message:
              'Er ging iets mis bij het annuleren van je afspraak. Als dit probleem zich blijft voordoen, neem telefonisch contact op met het ziekenhuis om je afspraak te annuleren.',
          })}
        </P>

        <Button variant='PrimaryWarning' onClick={cancelAppointment}>
          {t({
            id: 'appointments.modal.cancel.error.primary',
            comment: 'Modal primary button',
            message: 'Probeer het opnieuw',
          })}
        </Button>
        <Margin mt={theme.UI.Spacing.Space2} />
        <Button variant='Tertiary' onClick={handleClose} isLoading={isLoading}>
          {i18n._(globalDefinitions.goBack)}
        </Button>
      </>
    )
  }

  return (
    <Modal {...props}>
      <FlexColumn alignContent='center'>
        {error ? renderErrorContent() : value ? renderCancelledContent() : renderInitialContent()}
      </FlexColumn>
    </Modal>
  )
}

export { CancelAppointmentModal }
